import {
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Label from '../../../components/Label';
import { OrderServiceItem, ServiceVendor } from '../../../client/services';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getOrder } from '../../../store/order/api';
import { getAssetURl } from '../../../utils/url';
import { formatDate, fDateTime } from '../../../utils/formatTime';
import { formatPlainPrice } from '../../../utils/formatNumber';
import RedTimePicker from '../../DeliveryTimeSlots/RedTimePicker';
import { OrderStatus } from '../../../enum';
import AddOrderServiceDialog from '../components/AddOrderServiceDialog';

const LINE_STATUS: Record<
  string,
  { label: string; color: 'info' | 'success' | 'warning' | 'error' }
> = {
  pending: { label: 'Pending', color: 'warning' },
  confirmed: { label: 'Confirmed', color: 'success' },
  // Legacy partner steps — treat as Confirmed (Confirm/Reject only now)
  on_the_way: { label: 'Confirmed', color: 'success' },
  delivered: { label: 'Confirmed', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  cancelled: { label: 'Removed', color: 'error' },
};

const toYmd = (value: Date | null): string => {
  if (!value || Number.isNaN(value.getTime())) {
    return '';
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseYmd = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const firstProductImage = (line: any): string => {
  const images = line?.service_product?.images;
  if (Array.isArray(images) && images.length) {
    return String(images[0] || '');
  }
  return '';
};

/** How long the partner has been waiting to respond (from last WhatsApp / created). */
const formatWaitingDuration = (since: string | Date | null | undefined): string => {
  if (!since) {
    return '';
  }
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) {
    return '';
  }
  const totalSec = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  if (days > 0) {
    return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  return `${minutes} min`;
};

const pendingWaitSince = (line: any): string | null => {
  if (line?.lineStatus !== 'pending' || line?.unlinkedAt) {
    return null;
  }
  return line.lastWhatsappAt || line.createdAt || null;
};

const ServiceLineRow = ({
  line,
  idx,
  vendors,
  busy,
  relinkVendorId,
  setRelinkVendorId,
  runAction,
  handleOpenLightBox,
  orderDeliveryTime,
}: {
  line: any;
  idx: number;
  vendors: any[];
  busy: string | null;
  relinkVendorId: Record<string, string>;
  setRelinkVendorId: Dispatch<SetStateAction<Record<string, string>>>;
  runAction: (lineId: string, action: string, extra?: any) => Promise<void>;
  handleOpenLightBox?: (imageObject: any) => void;
  orderDeliveryTime?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<Date | null>(
    parseYmd(line.eventDate)
  );
  const [draftTime, setDraftTime] = useState<string>(
    String(orderDeliveryTime || '').trim()
  );
  const [, setTick] = useState(0);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const quantity = Number(line.quantity || line.qty || 0);
  const qtyOfProduct = Number(line.qtyOfProduct || 0);
  const unitPrice = Number(line.unitPrice || 0);
  const total = Number((quantity * unitPrice).toFixed(2));
  const status = LINE_STATUS[line.lineStatus] || {
    label: line.lineStatus,
    color: 'info' as const,
  };
  const imageKey = firstProductImage(line);
  const imageSrc = imageKey ? getAssetURl(imageKey, assetUrl) : '';
  const vendorName = line.vendorName || line.service_vendor?.name || '—';
  const vendorPhone = line.service_vendor?.mainMobile || '';
  const isCancelled = line.lineStatus === 'cancelled';
  const isRejected = line.lineStatus === 'rejected';
  const isConfirmed =
    line.lineStatus === 'confirmed' ||
    line.lineStatus === 'on_the_way' ||
    line.lineStatus === 'delivered';
  const isUnlinked = Boolean(line.unlinkedAt) || isRejected || isCancelled;
  const canResend = !isUnlinked && !isRejected && !isCancelled;
  const canAdminConfirm = !isCancelled && !isUnlinked && !isConfirmed;
  const canAdminReject = !isCancelled && !isUnlinked && !isRejected;
  const canChangeDate = !isCancelled && !isUnlinked;
  const canRemove = !isCancelled;
  const disabled = busy !== null;
  const lineBusy = Boolean(busy && busy.startsWith(`${line.id}:`));
  const waitSince = pendingWaitSince(line);
  const waitingDuration = waitSince ? formatWaitingDuration(waitSince) : '';

  useEffect(() => {
    if (!waitSince) {
      return undefined;
    }
    const id = window.setInterval(() => setTick((n) => n + 1), 30000);
    return () => window.clearInterval(id);
  }, [waitSince]);

  useEffect(() => {
    setDraftDate(parseYmd(line.eventDate));
  }, [line.eventDate]);

  useEffect(() => {
    setDraftTime(String(orderDeliveryTime || '').trim());
  }, [orderDeliveryTime]);

  return (
    <>
      <TableRow
        sx={{
          borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      >
        <TableCell align='left' sx={{ width: 56, px: 0.5 }}>
          <IconButton
            aria-label='expand service row'
            size='small'
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ width: 40, px: 1 }}>{idx + 1}</TableCell>
        <TableCell align='left' sx={{ width: 64, px: 1 }}>
          {imageSrc ? (
            <Avatar
              onClick={() => handleOpenLightBox?.(imageKey)}
              imgProps={{ crossOrigin: 'anonymous' }}
              src={imageSrc}
              sx={{
                borderRadius: 'unset',
                width: 48,
                height: 48,
                objectFit: 'contain',
                '& img': { objectFit: 'contain' },
                cursor: handleOpenLightBox ? 'pointer' : 'default',
              }}
            />
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell align='left' sx={{ overflow: 'hidden', px: 1 }}>
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant='subtitle2'
              noWrap
              title={
                line.productLabelEn || line.service_product?.labelEn || ''
              }
            >
              {line.productLabelEn || line.service_product?.labelEn || '—'}
            </Typography>
            {line.productLabelAr ? (
              <Typography
                variant='caption'
                color='text.secondary'
                noWrap
                display='block'
              >
                {line.productLabelAr}
              </Typography>
            ) : null}
          </Box>
        </TableCell>
        <TableCell
          align='left'
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', px: 1 }}
        >
          {line.variationLabelEn || line.service_product_variation?.labelEn || '—'}
        </TableCell>
        <TableCell
          align='left'
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', px: 1 }}
        >
          {isUnlinked ? '—' : vendorName}
        </TableCell>
        <TableCell align='left' sx={{ px: 1 }}>
          <Box>
            {line.eventDate ? formatDate(String(line.eventDate)) : '—'}
            {orderDeliveryTime ? (
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                {orderDeliveryTime}
              </Typography>
            ) : null}
          </Box>
        </TableCell>
        <TableCell align='right' sx={{ px: 1 }}>
          {quantity}
        </TableCell>
        <TableCell align='right' sx={{ px: 1 }}>
          {qtyOfProduct || '—'}
        </TableCell>
        <TableCell align='right' sx={{ px: 1 }}>
          {formatPlainPrice(unitPrice)}
        </TableCell>
        <TableCell align='right' sx={{ px: 1 }}>
          {formatPlainPrice(total)}
        </TableCell>
        <TableCell align='left' sx={{ px: 1 }}>
          <Stack spacing={0.5} alignItems='flex-start'>
            <Label color={status.color} variant='ghost'>
              {status.label}
            </Label>
            {waitingDuration ? (
              <Typography variant='caption' color='text.secondary'>
                Awaiting from last {waitingDuration}
              </Typography>
            ) : null}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ py: 2, px: 3 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Type</TableCell>
                    <TableCell align='center'>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell align='center'>Vendor</TableCell>
                    <TableCell align='center'>
                      {isUnlinked
                        ? vendorName && vendorName !== '—'
                          ? `Unlinked (was ${vendorName})`
                          : 'Unlinked'
                        : `${vendorName}${vendorPhone ? ` · ${vendorPhone}` : ''}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='center'>Special request</TableCell>
                    <TableCell align='center'>
                      {line.specialRequest || '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='center'>Last WhatsApp</TableCell>
                    <TableCell align='center'>
                      {line.lastWhatsappAt
                        ? fDateTime(line.lastWhatsappAt)
                        : '—'}
                    </TableCell>
                  </TableRow>
                  {waitingDuration ? (
                    <TableRow>
                      <TableCell align='center'>Waiting duration</TableCell>
                      <TableCell align='center'>
                        {waitingDuration}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                justifyContent='flex-end'
                flexWrap='wrap'
                sx={{ mt: 2 }}
              >
                {lineBusy ? <CircularProgress size={18} /> : null}
                {canAdminConfirm ? (
                  <Button
                    size='small'
                    variant='contained'
                    color='success'
                    disabled={disabled}
                    onClick={() =>
                      runAction(line.id, 'set_status', {
                        lineStatus: 'confirmed',
                      })
                    }
                  >
                    Confirm
                  </Button>
                ) : null}
                {canAdminReject ? (
                  <Button
                    size='small'
                    color='error'
                    variant='outlined'
                    disabled={disabled}
                    onClick={() =>
                      runAction(line.id, 'set_status', {
                        lineStatus: 'rejected',
                      })
                    }
                  >
                    Reject
                  </Button>
                ) : null}
                {canChangeDate ? (
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={disabled}
                    onClick={() => {
                      setDraftDate(parseYmd(line.eventDate));
                      setDraftTime(String(orderDeliveryTime || '').trim());
                      setDateDialogOpen(true);
                    }}
                  >
                    Change date
                  </Button>
                ) : null}
                {canRemove ? (
                  <Button
                    size='small'
                    color='error'
                    variant='outlined'
                    disabled={disabled}
                    onClick={() => setRemoveDialogOpen(true)}
                  >
                    Remove item
                  </Button>
                ) : null}
                {canResend ? (
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={disabled}
                    onClick={() => runAction(line.id, 'resend')}
                  >
                    Resend WhatsApp
                  </Button>
                ) : null}
                {isCancelled ? null : isUnlinked ? (
                  <>
                    <TextField
                      select
                      size='small'
                      label='Replacement vendor'
                      value={relinkVendorId[line.id] || ''}
                      onChange={(e) =>
                        setRelinkVendorId((prev) => ({
                          ...prev,
                          [line.id]: e.target.value,
                        }))
                      }
                      sx={{ minWidth: 220 }}
                    >
                      {vendors
                        .filter((v) => v.isActive)
                        .map((v) => (
                          <MenuItem key={v.id} value={v.id}>
                            {v.name}
                          </MenuItem>
                        ))}
                    </TextField>
                    <Button
                      size='small'
                      variant='contained'
                      disabled={!relinkVendorId[line.id] || disabled}
                      onClick={() =>
                        runAction(line.id, 'relink', {
                          vendorId: relinkVendorId[line.id],
                        })
                      }
                    >
                      Link replacement
                    </Button>
                  </>
                ) : (
                  <Button
                    type='button'
                    size='small'
                    color='error'
                    variant='outlined'
                    disabled={disabled}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void runAction(line.id, 'unlink');
                    }}
                  >
                    Unlink vendor
                  </Button>
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog
        open={dateDialogOpen}
        onClose={() => !disabled && setDateDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Change event date</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Partner will get a new WhatsApp and must confirm again. Customer will
            not get an “order changed” message. This updates the order delivery
            date/time for all event service lines on this order.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label='Event date'
              value={draftDate}
              onChange={(value: Date | null) => setDraftDate(value)}
              inputFormat='dd/MM/yyyy'
              renderInput={(params) => (
                <TextField {...params} fullWidth size='small' sx={{ mt: 1 }} />
              )}
            />
          </LocalizationProvider>
          <Box sx={{ mt: 2.5 }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 0.75 }}
            >
              Exact time slot
            </Typography>
            <RedTimePicker
              value={draftTime}
              onChange={setDraftTime}
              placeholder='Choose exact time'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDateDialogOpen(false)}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            disabled={disabled || !toYmd(draftDate) || !draftTime.trim()}
            onClick={async () => {
              const eventDate = toYmd(draftDate);
              if (!eventDate) {
                toast.error('Select a valid date');
                return;
              }
              if (!draftTime.trim()) {
                toast.error('Select an exact time');
                return;
              }
              await runAction(line.id, 'change_date', {
                eventDate,
                eventTime: draftTime.trim(),
              });
              setDateDialogOpen(false);
            }}
          >
            Confirm & notify partner
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeDialogOpen}
        onClose={() => !disabled && setRemoveDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Remove service item</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This service will be removed from the order. The partner confirm link
            will close. Refund is manual if needed. Other items on the order stay
            unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveDialogOpen(false)}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            color='error'
            variant='contained'
            disabled={disabled}
            onClick={async () => {
              await runAction(line.id, 'remove');
              setRemoveDialogOpen(false);
            }}
          >
            Remove item
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ServiceLinesPanel = ({
  order,
  onOrderUpdated,
  handleOpenLightBox,
}: {
  order: any;
  onOrderUpdated?: (order: any) => void;
  handleOpenLightBox?: (imageObject: any) => void;
}) => {
  const dispatch = useAppDispatch();
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const lines =
    order?.order_service_items ||
    order?.order_service_item ||
    order?.orderServiceItems ||
    [];
  const [vendors, setVendors] = useState<any[]>([]);
  const [relinkVendorId, setRelinkVendorId] = useState<Record<string, string>>(
    {}
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const awaiting = lines.some(
    (line: any) => line.lineStatus === 'pending' && !line.unlinkedAt
  );
  const canAddService =
    (userRole === 'admin' || userRole === 'orderManager') &&
    order?.status !== OrderStatus.cancelled &&
    order?.status !== OrderStatus.delivered;

  useEffect(() => {
    ServiceVendor.find({ query: {} })
      .then((res: any) => setVendors(res.data || []))
      .catch(() => setVendors([]));
  }, []);

  const refresh = async () => {
    const record = await dispatch(getOrder({ id: order.id })).unwrap();
    onOrderUpdated?.(record);
  };

  if (!lines.length && !canAddService) {
    return (
      <Box sx={{ py: 2, px: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          No event service lines on this order.
        </Typography>
      </Box>
    );
  }

  const runAction = async (lineId: string, action: string, extra: any = {}) => {
    if (!lineId) {
      toast.error('Service line is missing');
      return;
    }
    setBusy(`${lineId}:${action}`);
    try {
      const result = await OrderServiceItem.patch(lineId, { action, ...extra });
      if (result?.confirmUrl) {
        toast.success(`WhatsApp queued. Confirm URL: ${result.confirmUrl}`);
      } else if (action === 'unlink') {
        toast.success('Vendor unlinked. Choose a replacement vendor.');
      } else if (action === 'change_date') {
        toast.success(
          'Event date/time updated. Partner notified (no reconfirm required).'
        );
      } else if (action === 'remove') {
        toast.success('Service item removed. Partner link closed.');
      } else if (action === 'set_status') {
        toast.success(
          extra?.lineStatus === 'confirmed'
            ? 'Service marked Confirmed'
            : 'Service marked Rejected'
        );
      } else {
        toast.success(`Line ${action} updated`);
      }
      await refresh();
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          err?.data?.detail ||
          err?.message ||
          `Failed to ${action}`
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ mt: 5, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2, px: 1 }}
        spacing={1}
      >
        <Box>
          <Typography
            paragraph
            variant='overline'
            sx={{ color: 'text.disabled', mb: 0.5 }}
          >
            Event services
          </Typography>
          <Typography variant='subtitle1'>
            {lines.length} service item{lines.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <Stack direction='row' alignItems='center' spacing={1}>
          {awaiting ? (
            <Label color='warning' variant='ghost'>
              Awaiting vendor
            </Label>
          ) : null}
          {canAddService ? (
            <Button
              size='small'
              variant='outlined'
              onClick={() => setAddServiceOpen(true)}
            >
              Add service
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {lines.length ? (
        <TableContainer sx={{ width: '100%', overflowX: 'hidden' }}>
          <Table
            size='small'
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& th, & td': {
                py: 1,
                verticalAlign: 'middle',
              },
            }}
          >
            <TableHead
              sx={{
                borderBottom: (theme) =>
                  `solid 1px ${theme.palette.divider}`,
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 56, px: 0.5 }} />
                <TableCell sx={{ width: 40, px: 1 }}>#</TableCell>
                <TableCell align='left' sx={{ width: 64, px: 1 }}>
                  Image
                </TableCell>
                <TableCell align='left' sx={{ width: '13%', px: 1 }}>
                  Service
                </TableCell>
                <TableCell align='left' sx={{ width: '10%', px: 1 }}>
                  Variation
                </TableCell>
                <TableCell align='left' sx={{ width: '12%', px: 1 }}>
                  Vendor
                </TableCell>
                <TableCell align='left' sx={{ width: '10%', px: 1 }}>
                  Event date
                </TableCell>
                <TableCell align='right' sx={{ width: '7%', px: 1 }}>
                  Qty
                </TableCell>
                <TableCell align='right' sx={{ width: '9%', px: 1 }}>
                  Qty of product
                </TableCell>
                <TableCell align='right' sx={{ width: '8%', px: 1 }}>
                  Unit Price
                </TableCell>
                <TableCell align='right' sx={{ width: '8%', px: 1 }}>
                  Total
                </TableCell>
                <TableCell align='left' sx={{ width: '9%', px: 1 }}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line: any, idx: number) => (
                <ServiceLineRow
                  key={line.id}
                  line={line}
                  idx={idx}
                  vendors={vendors}
                  busy={busy}
                  relinkVendorId={relinkVendorId}
                  setRelinkVendorId={setRelinkVendorId}
                  runAction={runAction}
                  handleOpenLightBox={handleOpenLightBox}
                  orderDeliveryTime={
                    order?.deliveryDate?.time
                      ? String(order.deliveryDate.time)
                      : ''
                  }
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ py: 1, px: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            No event service lines on this order yet.
          </Typography>
        </Box>
      )}
      {order?.id ? (
        <AddOrderServiceDialog
          open={addServiceOpen}
          orderId={order.id}
          onClose={() => setAddServiceOpen(false)}
          onAdded={refresh}
        />
      ) : null}
    </Box>
  );
};

export default ServiceLinesPanel;
